import { createContext, useContext, useCallback, useMemo, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { logger } from '@/utils/logger';

/**
 * AdminContext - Optimized with React Query
 *
 * PERFORMANCE FIX: Eliminates sequential queries on every page load
 *
 * **Before (Sequential - 3 DB roundtrips):**
 * T=0ms    getUser() starts
 * T=100ms  getUser() completes
 * T=100ms  is_admin RPC starts (waited 100ms!)
 * T=200ms  is_admin RPC completes
 * T=200ms  user_roles query starts (waited 200ms!)
 * T=300ms  user_roles completes
 * Total: ~300ms of sequential queries on EVERY page load
 *
 * **After (Cached with React Query):**
 * - First load: ~200ms (parallel queries)
 * - Subsequent: ~0ms (from cache, staleTime: 5min)
 * - Uses user from AuthContext (no extra getUser call)
 *
 * @version 2.0.0
 * @date 2025-12-29
 */

interface AdminContextType {
  isAdmin: boolean;
  loading: boolean;
  userRoles: string[];
  checkingRole: boolean;
  checkAdminStatus: () => Promise<void>;
  refreshRoles: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

// Query keys for React Query cache management
const adminQueryKeys = {
  all: ['admin'] as const,
  isAdmin: (userId: string) => [...adminQueryKeys.all, 'isAdmin', userId] as const,
  roles: (userId: string) => [...adminQueryKeys.all, 'roles', userId] as const,
};

interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider = ({ children }: AdminProviderProps) => {
  // PERF FIX: Use user from AuthContext instead of calling getUser() again
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // PERF FIX: Check admin status with React Query caching
  // Runs in parallel with roles query, cached for 5 minutes
  const {
    data: isAdmin = false,
    isLoading: isLoadingAdmin,
  } = useQuery({
    queryKey: adminQueryKeys.isAdmin(user?.id ?? ''),
    queryFn: async () => {
      if (!user?.id) return false;

      const { data, error } = await supabase
        .rpc('is_admin', { p_user_id: user.id });

      if (error) {
        logger.error('Error checking admin status:', error);
        return false;
      }

      return data || false;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes - admin status doesn't change often
    gcTime: 15 * 60 * 1000,   // 15 minutes cache retention
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Use cached data on re-mount
  });

  // PERF FIX: Fetch user roles with React Query caching
  // Runs in PARALLEL with isAdmin query (not sequential!)
  const {
    data: userRoles = [],
    isLoading: isLoadingRoles,
  } = useQuery({
    queryKey: adminQueryKeys.roles(user?.id ?? ''),
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (error) {
        logger.error('Error fetching user roles:', error);
        return [];
      }

      return data.map(r => r.role);
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000,   // 15 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Combined loading state
  const loading = isLoadingAdmin || isLoadingRoles;

  // Refresh admin status and roles
  const refreshRoles = useCallback(async () => {
    if (!user?.id) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.isAdmin(user.id) }),
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.roles(user.id) }),
    ]);
  }, [user?.id, queryClient]);

  // Alias for backward compatibility
  const checkAdminStatus = refreshRoles;

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<AdminContextType>(() => ({
    isAdmin,
    loading,
    userRoles,
    checkingRole: loading,
    checkAdminStatus,
    refreshRoles,
  }), [isAdmin, loading, userRoles, checkAdminStatus, refreshRoles]);

  return (
    <AdminContext.Provider value={contextValue}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

// Higher-order component for admin-only routes
export const withAdminAuth = <P extends object>(
  Component: React.ComponentType<P>
) => {
  const WrappedComponent = (props: P) => {
    const { isAdmin, loading } = useAdmin();

    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!isAdmin) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You need admin privileges to access this area.</p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };

  WrappedComponent.displayName = `withAdminAuth(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};